import $ from 'jquery'
import Backbone from 'backbone'
import template from './file-drop.hbs'
import i18n from 'scoreboard/components/i18n'
import _ from 'lodash'
import FileUploader from './file-uploader'
import AnimatedFileUploader from 'scoreboard/components/files/animated-file-uploader'
import {isMissingColumnsException} from 'scoreboard/imports/new-column-name-chooser-wizard'

const FileDrop = Backbone.View.extend({

	className: 'file-drop',

	events: {
		'dragover': 'onDragover',
		'dragleave': 'onDragleave',
		'drop': 'onDrop',
		'click': 'onClick'
	},

	/**
	 * A FileDrop is a visible, responsive, drop zone which can handle file drops. This component works with a FileUploader,
	 * in order to display the different stages of the upload process. A FileUploader can be passed in or configured with
	 * passed in options.
	 *
	 * Usage:
	 *
	 * // Created by passing in file uploader options
	 * let fileDrop = new FileDrop({
	 *   url: '/place/to/upload',
	 *   [optional file-uploader options]
	 * })
	 *
	 * // Created by passing in a custom file uploader
	 * let fileDrop = new FileDrop({
	 *   fileUploader: new CustomUploader({ [uploader options] })
	 * })
	 *
	 * IMPORTANT: Do not place the file drop inside a <label> or else:
	 *  1. it'll cause a bug in safari #14551
	 *  2. the <label> will catch clicks because the <input type=file> is inside it,
	 *  causing the file chooser to deploy twice #38131.
	 *
	 * @param {FileUploader} [options.fileUploader] A custom file uploader which is to work in tandem with this file drop.
	 * One might want to use a custom uploader for greater control over it. This comes in handy if there's any functionality
	 * needed out side the usual drag/drop/upload/finish-or-error flow
	 * @param {String} [options.noDropMessage] The message to be rendered when dropping isn't possible.
	 * @param {String} [options.dropLabel] The drop label to be rendered
	 * @param {String} [options.dropSubLabel] The drop sub label to be rendered
	 * @param {Boolean} [options.animated] If true, use an animated file uploader, else use a regular one
	 * @param {Boolean} [options.revisionDrop ] If this is a file dropped as a
	 * revision on the documents-overview page
	 * @param {FileUploderOptions} options.fileUploaderOptions options which pertain to the file-uploader that is associated with
	 * this file-drop
	 */

	initialize(options = {}) {
		this.options = options

		if (this.options.fileUploader) {
			this.fileUploader = this.options.fileUploader
		} else {
			if (this.options.animated) {
				this.fileUploader = new AnimatedFileUploader(this.options.fileUploaderOptions)
			} else {
				this.fileUploader = new FileUploader(this.options.fileUploaderOptions)
			}
		}

		_.bindAll(this, '_captureDocumentDrop')
	},

	onDragover(e) {
		e.preventDefault()
		e.stopPropagation()
		this.$el.addClass('is-droppable')
		// change appearance of mouse cursor
		e.originalEvent.dataTransfer.dropEffect = 'copy'
	},

	onDragleave() {
		this.$el.removeClass('is-droppable')
	},

	onDrop(e) {
		e.preventDefault()
		e.stopPropagation()
		this.fileUploader.onDrop(e)
		this.$el.addClass('is-uploading')
	},

	onClick() {
		this.fileUploader.chooseFile()
		this.trigger('click')
	},

	render() {
		this.$el.empty().append(template(this.context()))
		this._renderFileUploader()
		this._attachListeners()

		if (this.fileUploader.getFile()) {
			this.$el.addClass('has-file')
		}
		return this
	},

	// Capture the document's 'drop' event so that it doesn't override
	// this component's drop event.
	_captureDocumentDrop(e) {
		e.preventDefault()
		e.stopPropagation()
	},

	_attachListeners() {
		$(document).on('drop', this._captureDocumentDrop)

		this.fileUploader.on('done', data => this._handleFileDone(data))
			.on('fileChosen', data => this._handleFileChosen(data))
			.on('error', xhr => this._handleFileError(xhr))
			.on('start', data => this.trigger('start', data))
			.on('extensionError', err => {
				// This error does not come from the backend. Bump it to the next tick
				// so that the drop/file selection event which caused it can finish.
				process.nextTick(() => this._handleFileError(err))
			})
			.on('reset', () => this._reset())
	},

	_renderFileUploader() {
		this.$el.after(this.fileUploader.el)
		this.fileUploader.render()
	},

	_handleFileDone(data) {
		this.trigger('done', data)
	},

	_handleFileChosen(data) {
		this.$el.addClass('is-uploading')
		this.trigger('fileChosen', data)
	},

	_handleFileError(xhr) {
		// #60043 if this is a missing columns exception for a revision dropped
		// on documents-overview, it will have been handled by upload-form.js
		// and there will be a next step in the wizard, so we don't want
		// to reset the appearance or do any other error cleanup on the file-drop
		// This is similar to the logic added in #58698 to the file-uploader.js
		// resetAppearance method
		if (this.options.revisionDrop) {
			const isMissingColumnsEx = isMissingColumnsException(xhr)
			_.invoke(xhr, 'abort')
			if (isMissingColumnsEx) return
		}

		this.reset()
		this.trigger('error', xhr)
	},

	_reset() {
		this.reset()
		this.trigger('reset')
	},

	reset() {
		this.$el.removeClass('is-uploading is-droppable has-file')
		this.fileUploader.resetAppearance()
	},

	cancel() {
		this.reset()
		this.fileUploader.cancel()
	},

	context() {
		return _.extend({}, {
			dropLabel: i18n.get('documents.upload.drop.document.1'),
			dropSubLabel: i18n.get('documents.upload.drop.document.2')
		}, this.options)
	},

	getFiles() {
		return this.fileUploader.files
	},

	remove() {
		Backbone.View.prototype.remove.apply(this, arguments)
		$(document).off('drop', this._captureDocumentDrop)
		_.invoke(this.fileUploader, 'cancel')
		_.invoke(this.fileUploader, 'remove')
	}
})

export default FileDrop
FileDrop.__filename__ = 'components/files/file-drop.js'
